import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Grid, Box, Text, ThemeUIStyleObject } from 'theme-ui';

import { logSentryError } from '~/sentry';
import { useCustomAnalytics } from '~/hooks/utils/customAnalytics';
import InputWithButton from '../Form/InputWithButton';
import InputWithPlaceholder from '../Form/InputWithPlaceholder';
import ValidationError from './ValidationError';
import { useTranslate } from '~/hooks/utils/use-translate';
type Props = {
  forwardSx?: ThemeUIStyleObject;
  placeholder: Queries.ModalFragmentFragment['emailFieldPlaceholder'];
  theme?: 'dark' | 'light';
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  placement?: string;
  showEmailInputField?: Queries.ModalFragmentFragment['showEmailInputField'];
  showDoBInputField?: Queries.ModalFragmentFragment['showDoBInputField'];
  showNameInputFields?: Queries.ModalFragmentFragment['showNameInputFields'];
  confirmationMessage?: Queries.ModalFragmentFragment['confirmationMessage'];
  isFooter?: boolean;
  isSubmitted?: MutableRefObject<boolean>;
};

export type FormValues = {
  email: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
};
const SignupForm = ({
  forwardSx = {},
  placeholder,
  theme = 'dark',
  setIsOpen,
  placement,
  showEmailInputField,
  showDoBInputField,
  showNameInputFields,
  confirmationMessage,
  isFooter,
  isSubmitted,
}: Props) => {
  const translate = useTranslate();
  const { trackEmailCaptured } = useCustomAnalytics();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const [submitted, setSubmitted] = useState(isSubmitted?.current ?? false);
  const [apiError, setApiError] = useState<{ type: string; message: string } | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onSubmit = async (data: FormValues, event: React.BaseSyntheticEvent<object, any, any> | undefined) => {
    const { email, firstName, lastName, dateOfBirth } = data;
    event?.preventDefault();
    setApiError(null);
    try {
      await trackEmailCaptured({
        email,
        firstName,
        lastName,
        dateOfBirth,
        locationDetail: placement ?? 'email_signup',
      });
      setSubmitted(true);
      if (setIsOpen) {
        timeoutRef.current = setTimeout(() => {
          setIsOpen(false);
        }, 2500);
      }
      if (isSubmitted) {
        isSubmitted.current = true;
      }
    } catch (err) {
      setApiError({
        type: 'api',
        message: translate('error.api.default'),
      });
      logSentryError(err, { source: `email_signup_${placement}` });
    }
  };

  useEffect(() => {
    if (timeoutRef.current) {
      return clearTimeout(timeoutRef.current);
    }
  }, []);

  return (
    <Grid
      key="email-signup-form"
      as="form"
      onSubmit={handleSubmit(async (data, event) => await onSubmit(data, event))}
      sx={{
        position: 'relative',
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '.8rem',
        ...forwardSx,
      }}
    >
      {showNameInputFields && (
        <>
          <Box>
            <InputWithPlaceholder
              // @ts-ignore
              placeholder="First Name"
              forwardSx={{ fontSize: '1.6rem' }}
              theme={theme}
              {...register('firstName', { required: true })}
            />
            <ValidationError error={errors?.firstName} />
          </Box>
          <Box>
            <InputWithPlaceholder
              // @ts-ignore
              placeholder="Last Name"
              forwardSx={{ fontSize: '1.6rem' }}
              theme={theme}
              {...register('lastName', { required: true })}
            />
            <ValidationError error={errors?.lastName} />
          </Box>
        </>
      )}
      {showDoBInputField && (
        <Box>
          <InputWithPlaceholder
            // @ts-ignore
            placeholder="Date of Birth"
            forwardSx={{ fontSize: '1.6rem' }}
            theme={theme}
            type="date"
            {...register('dateOfBirth', { required: true })}
          />
          <ValidationError error={errors?.dateOfBirth} />
        </Box>
      )}
      {(showEmailInputField || isFooter) && (
        <Grid sx={{ gridColumn: '1/3', minHeight: '4rem' }}>
          {submitted ? (
            <Text
              sx={{
                fontWeight: 'medium',
                fontSize: '1.6rem',
                textAlign: 'center',
                textTransform: 'uppercase',
                width: '100%',
              }}
            >
              {confirmationMessage ?? "Thanks you're on the list!"}
            </Text>
          ) : (
            <>
              <InputWithButton
                // @ts-ignore
                theme={theme}
                placeholder={placeholder}
                forwardSx={{ fontSize: '1.6rem' }}
                buttonText="Submit"
                {...register('email', {
                  required: true,
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: translate('validation.invalid_email'),
                  },
                })}
              />
              <ValidationError error={errors?.email} />
            </>
          )}
        </Grid>
      )}
      <ValidationError error={apiError} />
    </Grid>
  );
};

export default SignupForm;
